import React, { useState, useEffect } from 'react';
import { usePrimaryLedger } from '@/components/primary-ledger/PrimaryLedgerContext';
import { DateRangeSelector, DateRangeValue } from '@/components/primary-ledger/DateRangeSelector';
import SalesProfitSection from '@/components/primary-ledger/SalesProfitSection';
import { PPCResultsSection } from '@/components/primary-ledger/PPCResultsSection';
import { MarginSection } from '@/components/primary-ledger/MarginSection';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { primaryLedgerService } from '@/services/primaryLedgerService';
import { SalesProfitHistory, PPCResults, MarginHistory } from '@/types/primaryLedger';

const Dashboard: React.FC = () => {
  const { selectedCompany, selectedAsin, companies } = usePrimaryLedger();
  
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    preset: 'last_30_days',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });

  const [salesProfitData, setSalesProfitData] = useState<SalesProfitHistory[]>([]);
  const [ppcData, setPpcData] = useState<PPCResults[]>([]);
  const [marginData, setMarginData] = useState<MarginHistory[]>([]);
  
  const [loadingSalesProfit, setLoadingSalesProfit] = useState(false);
  const [loadingPPC, setLoadingPPC] = useState(false);
  const [loadingMargin, setLoadingMargin] = useState(false);
  
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedCompany || !selectedAsin) {
        setSalesProfitData([]);
        setPpcData([]);
        setMarginData([]);
        return;
      }

      // Get company object to access companyName
      const company = companies.find(c => c.id === selectedCompany);
      if (!company) {
        console.error('Company not found:', selectedCompany);
        setError('Company not found');
        return;
      }

      setError(null);

      try {
        setLoadingSalesProfit(true);
        const salesProfit = await primaryLedgerService.getSalesProfitHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined
        );
        setSalesProfitData(salesProfit);
      } catch (err) {
        console.error('Error fetching sales/profit data:', err);
        setError('Failed to load sales and profit data');
      } finally {
        setLoadingSalesProfit(false);
      }

      try {
        setLoadingPPC(true);
        const ppc = await primaryLedgerService.getPPCResults(
          selectedCompany,
          company.companyName,
          selectedAsin,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined
        );
        setPpcData(ppc);
      } catch (err) {
        console.error('Error fetching PPC data:', err);
      } finally {
        setLoadingPPC(false);
      }

      try {
        setLoadingMargin(true);
        const margin = await primaryLedgerService.getMarginHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined
        );
        setMarginData(margin);
      } catch (err) {
        console.error('Error fetching margin data:', err);
      } finally {
        setLoadingMargin(false);
      }
    };

    fetchData();
  }, [selectedCompany, selectedAsin, dateRange.startDate, dateRange.endDate, companies]);

  if (!selectedCompany || !selectedAsin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-600" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No Company or ASIN Selected
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                  Please select a company and ASIN from the side panel to view performance data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {companies.find(c => c.id === selectedCompany)?.companyName || selectedCompany} - {selectedAsin}
          </p>
        </div>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {error && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-8">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Sales & Profit History
          </h2>
          <SalesProfitSection data={salesProfitData} loading={loadingSalesProfit} />
        </section>

        <section>
          <PPCResultsSection data={ppcData} loading={loadingPPC} />
        </section>

        <section>
          <MarginSection data={marginData} loading={loadingMargin} />
        </section>
      </div>
    </div>
  );
};

export default Dashboard;